import { em, important, percent, px, rem, url } from 'csx';
import React from 'react';
import { classes, style } from 'typestyle';
import { inject, deps, observer, Component } from '../../lib/component';
import { colorDarkGreen, colorGreyAccent, colorLightBlueGrey, colorLightGrey } from '../../theme/color';
import { ButtonPill } from '../button-pill';
import { Collapsible } from '../collapsible/collapsible';
import { styles as checkoutStyles, borderStyle } from './styles';
import { mediaMobileOnly, mediaTablet } from '../../theme/media';
import UserStatusBar from '../user/status-bar';
import { RatecardGauge } from '../ratecard-gauge';
import { SVGIcon } from '../svg-icon';
import { shadowElevation2 } from '../../theme/shadow';

const speedVal = 0.25;
const speed = `${speedVal}s`;
const func = 'ease-in-out';
const transition = (...properties: string[]) => properties.map((property) => `${speed} ${property} ${func}`).join(', ');
const styles = {
  loginCta: style(
    {
      padding: px(16),
      borderBottom: borderStyle,
      textAlign: 'center',
      position: 'relative',
      $nest: {
        '& p:first-child': {
          margin: `${px(16)} ${px(0)} ${px(4)}`,
          lineHeight: px(16),
          fontSize: px(12),
        },
      },
    },
    mediaMobileOnly({
      $nest: {
        '& p:first-child': {
          marginTop: 0,
        },
      },
    }),
  ),
  wrapper: style({
    padding: px(16),
    position: 'relative',
    zIndex: 1,
  }),
  wrapperFloating: style({
    height: px(139),
  }),
  ratecard: style({
    width: percent(61),
    margin: '15px auto',
    minWidth: px(178),
    $nest: {
      '& > div:first-child': {
        width: percent(100),
        margin: 0,
        backgroundColor: 'white',
      },
      '& > div:nth-child(3)': {
        fontWeight: 'normal',
        fontSize: px(16),
      },
    },
  }),
  collapsibleWrapper: style(
    shadowElevation2,
    {
      backgroundColor: colorLightBlueGrey.toHexString(),
    },
    mediaTablet({
      maxWidth: px(320),
      margin: '0 auto',
    }),
  ),
  summaryWrapper: style({
    padding: 0,
  }),
  topPanel: style({
    display: 'block',
    position: 'relative',
    borderBottomWidth: px(1),
    borderBottomStyle: 'solid',
    borderBottomColor: colorLightGrey.toHexString(),
    padding: '16px',
  }),
  title: style({
    fontSize: px(16),
    margin: 0,
  }),
  stateIndicator: style({ position: 'absolute', right: px(16), top: px(7) }),
  middlePanel: style({
    textAlign: 'left',
    position: 'relative',
    borderBottomWidth: px(1),
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    transition: `${speed} border-bottom-color ${func}`,
    $nest: {
      '.open &': {
        borderBottomColor: colorLightGrey.toHexString(),
      },
    },
  }),
  displayPhoto: style({
    width: px(28),
    marginLeft: px(16),
    marginRight: px(8),
    $nest: {
      '&, & > div': {
        transition: transition('width', 'height'),
      },
      '& > div': {
        width: px(24),
        height: px(24),
      },
      '.open &': {
        width: important('112px'),
        height: px(112),
        $nest: {
          '& > div': {
            width: rem(5),
            height: rem(5),
          },
        },
      },
    },
  }),
  username: style({
    transition: transition('flex-grow', 'font-size', 'transform', 'width', 'margin-right', 'margin-top'),
    textAlign: 'center',
    overflow: 'hidden',
    flexGrow: 1,
    width: 'calc(100% - 174px)',
    marginRight: px(127),
    $nest: {
      '.open &': {
        flexGrow: 0,
        fontSize: px(16),
        width: px(153),
        transform: 'translateX(-15px)',
        marginTop: em(1),
        marginRight: 0,
      },
    },
  }),
  usernameInner: style({
    transition: transition('width'),
    display: 'block',
    height: em(1.1625),
    whiteSpace: 'nowrap',
    width: 0,
    $nest: {
      '.open &': {
        width: percent(100),
      },
    },
  }),
  channelTextWrapper: style({
    position: 'absolute',
    textAlign: 'center',
    width: px(153),
    right: px(16),
    top: px(10),
  }),
  channelText: style({
    margin: 0,
    opacity: 0,
    transition: `${speedVal / 2}s opacity ${func}`,
    fontWeight: 'normal',
    $nest: {
      '.open &': {
        opacity: 1,
        transition: `${speed} ${speed} opacity ${func}`,
      },
    },
  }),
  activeText: style({
    color: colorDarkGreen.toHexString(),
  }),
  changeButton: style({
    transition: transition('bottom', 'font-size', 'padding'),
    position: 'absolute',
    right: px(16),
    bottom: px(16),
    fontSize: px(12),
    $nest: {
      '.closed &': {
        padding: '2px 10px',
      },
      '.open &': {
        bottom: px(7),
        fontSize: px(16),
      },
    },
  }),
};

interface CheckoutUserPanelProps {
  className?: string;
  handleSignIn: (event: any, ref?: any) => void;
  handleChannelToggle?: (event: any, ref?: any) => void;
  showToggle?: boolean;
  floating?: boolean;
}

@inject(deps)
@observer
export default class CheckoutUserPanel extends Component<CheckoutUserPanelProps> {
  render() {
    const { model, controller, className, handleSignIn, handleChannelToggle, showToggle, floating = true } = this.props;
    const { user } = model.auth;
    const { channel } = model.user;

    const avatar = channel && channel.images && channel.images.length ? channel.images[0].url : null;

    if (!user) {
      return (
        <div className={classes(styles.loginCta, className)}>
          <p>
            Create a <strong>free</strong> account to continue
          </p>
          <ButtonPill primary onClick={handleSignIn} className={checkoutStyles.loginBtn}>
            Let&apos;s go
          </ButtonPill>
        </div>
      );
    }

    return (
      <div className={classes(styles.wrapper, floating && styles.wrapperFloating, className)}>
        <Collapsible className={styles.collapsibleWrapper} show={showToggle}>
          <Collapsible.Summary className={styles.summaryWrapper} onToggle={handleChannelToggle}>
            <div className={classes(Collapsible.styles.summaryLabel, styles.topPanel)}>
              <h5 className={styles.title}>Your Channel</h5>
              <Collapsible.StateIndicator className={styles.stateIndicator} />
            </div>
            <div className={classes(Collapsible.styles.summaryLabel, styles.middlePanel)}>
              <div className={classes(UserStatusBar.styles.avatar, styles.displayPhoto)}>
                <div style={{ backgroundImage: url(avatar) }} />
              </div>
              <h4 className={styles.username}>
                <span className={styles.usernameInner}>{channel.name}</span>
              </h4>
              <span className={styles.channelTextWrapper}>
                <p className={classes(styles.channelText, styles.activeText)}>
                  <SVGIcon.CheckmarkCircle color={colorDarkGreen.toHexString()} size={10} /> Active
                </p>
                <p className={styles.channelText}>YouTube Channel</p>
              </span>
              <ButtonPill className={styles.changeButton} onClick={this.showChannelSelect.bind(this)}>
                Change channel
              </ButtonPill>
            </div>
          </Collapsible.Summary>
          <RatecardGauge
            className={styles.ratecard}
            ratecard={model.checkout.channel.ratecard}
            statistics={model.checkout.channel.ratecard_statistics}
          />
        </Collapsible>
      </div>
    );
  }

  showChannelSelect(evt: React.MouseEvent<any>) {
    evt.stopPropagation();
    this.props.controller.modal.showChannelSelectModal(2);
  }
}
